import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, ShieldOff, RotateCw, Eye, EyeOff, Copy, Check, X } from 'lucide-react';
import { apiService } from '../services/api';
import type { ProviderKey, AgentKey } from '../types';

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-gray-400 hover:text-indigo-600 transition-colors">
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
}

function RevealOnceModal({ token, onClose }: { token: string; onClose: () => void }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Save Your Token</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          ⚠️ This token will <strong>not</strong> be shown again. Copy it now and store it securely.
        </div>
        <div className="flex items-center gap-2 bg-gray-50 rounded-md border border-gray-200 px-3 py-2">
          <code className="flex-1 text-sm font-mono text-gray-800 break-all">
            {visible ? token : token.slice(0, 12) + '••••••••••••••••••••••••••••'}
          </code>
          <button onClick={() => setVisible(v => !v)} className="text-gray-400 hover:text-gray-700">
            {visible ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <CopyButton value={token} />
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Done — I've saved it
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    revoked: 'bg-red-100 text-red-600',
    rotated: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

export default function ApiKeys() {
  const [providerKeys, setProviderKeys] = useState<ProviderKey[]>([]);
  const [agentKeys, setAgentKeys] = useState<AgentKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState<string | null>(null);

  // New provider key form
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [pProvider, setPProvider] = useState('openai');
  const [pRawKey, setPRawKey] = useState('');
  const [pSubmitting, setPSubmitting] = useState(false);

  // New agent key form
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [aAgentId, setAAgentId] = useState('');
  const [aScopes, setAScopes] = useState('chat:completions');
  const [aQuota, setAQuota] = useState(60);
  const [aSubmitting, setASubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pk, ak] = await Promise.all([apiService.listProviderKeys(), apiService.listAgentKeys()]);
      setProviderKeys(pk);
      setAgentKeys(ak);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createProviderKey = async () => {
    if (!pProvider || !pRawKey) return;
    setPSubmitting(true);
    try {
      await apiService.createProviderKey(pProvider, pRawKey);
      setPRawKey(''); setShowProviderForm(false); await load();
    } finally { setPSubmitting(false); }
  };

  const createAgentKey = async () => {
    if (!aAgentId) return;
    setASubmitting(true);
    try {
      const result = await apiService.createAgentKey(aAgentId, aScopes.split(',').map(s => s.trim()), aQuota);
      setNewToken(result.token);
      setAAgentId(''); setShowAgentForm(false); await load();
    } finally { setASubmitting(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
        <p className="mt-1 text-sm text-gray-500">Manage provider credentials and agent access tokens</p>
      </div>

      {/* Provider Keys */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center">
          <Key size={16} className="text-indigo-500 mr-2" />
          <h2 className="font-semibold text-gray-900">Provider Keys</h2>
          <div className="flex-1" />
          <button
            onClick={() => setShowProviderForm(s => !s)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus size={13} /> Add Key
          </button>
        </div>

        {showProviderForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Provider</label>
              <select value={pProvider} onChange={e => setPProvider(e.target.value)} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {['openai', 'google', 'anthropic'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">Raw API Key (will not be stored)</label>
              <input type="password" value={pRawKey} onChange={e => setPRawKey(e.target.value)} placeholder="sk-…" className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <button onClick={createProviderKey} disabled={pSubmitting || !pRawKey} className="px-4 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
              {pSubmitting ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setShowProviderForm(false)} className="px-3 py-1.5 text-sm text-gray-500">Cancel</button>
          </div>
        )}

        {loading ? <div className="p-8 text-center text-sm text-gray-400">Loading...</div> : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>{['Provider', 'Key (masked)', 'Status', 'Created', ''].map(h => <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {providerKeys.map(k => (
                <tr key={k.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800">{k.provider}</td>
                  <td className="py-3 px-4 font-mono text-sm text-gray-500">{k.keyPrefix}</td>
                  <td className="py-3 px-4"><StatusBadge status={k.status} /></td>
                  <td className="py-3 px-4 text-xs text-gray-400">{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    {k.status === 'active' && (
                      <div className="flex items-center gap-3">
                        <button onClick={async () => { await apiService.rotateProviderKey(k.id, window.prompt('New raw key:') ?? ''); load(); }} className="flex items-center gap-1 text-xs text-yellow-600 hover:text-yellow-800">
                          <RotateCw size={13} /> Rotate
                        </button>
                        <button onClick={async () => { if (window.confirm('Revoke this key?')) { await apiService.revokeProviderKey(k.id); load(); } }} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
                          <ShieldOff size={13} /> Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {providerKeys.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-gray-400">No provider keys</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Agent Keys */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center">
          <Key size={16} className="text-purple-500 mr-2" />
          <h2 className="font-semibold text-gray-900">Agent API Keys</h2>
          <div className="flex-1" />
          <button onClick={() => setShowAgentForm(s => !s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700">
            <Plus size={13} /> Create Key
          </button>
        </div>

        {showAgentForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Agent ID</label>
              <input value={aAgentId} onChange={e => setAAgentId(e.target.value)} placeholder="aegis-cam-01" className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Scopes (comma-sep)</label>
              <input value={aScopes} onChange={e => setAScopes(e.target.value)} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Quota (RPM)</label>
              <input type="number" value={aQuota} onChange={e => setAQuota(Number(e.target.value))} className="w-20 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <button onClick={createAgentKey} disabled={aSubmitting || !aAgentId} className="px-4 py-1.5 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
              {aSubmitting ? 'Creating…' : 'Create'}
            </button>
            <button onClick={() => setShowAgentForm(false)} className="px-3 py-1.5 text-sm text-gray-500">Cancel</button>
          </div>
        )}

        {loading ? <div className="p-8 text-center text-sm text-gray-400">Loading...</div> : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>{['Agent ID', 'Scopes', 'Quota (RPM)', 'Status', 'Created', ''].map(h => <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agentKeys.map(k => (
                <tr key={k.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm text-gray-800">{k.agentId}</td>
                  <td className="py-3 px-4 text-xs text-gray-500">{(k.scopes ?? []).join(', ')}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{k.quotaRpm}</td>
                  <td className="py-3 px-4"><StatusBadge status={k.status} /></td>
                  <td className="py-3 px-4 text-xs text-gray-400">{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    {k.status === 'active' && (
                      <button onClick={async () => { if (window.confirm('Revoke this agent key?')) { await apiService.revokeAgentKey(k.id); load(); } }} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
                        <ShieldOff size={13} /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {agentKeys.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-gray-400">No agent keys</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {newToken && <RevealOnceModal token={newToken} onClose={() => setNewToken(null)} />}
    </div>
  );
}
